// Centralisation des blocs horaires et services pour les pros

export type TimeBlockKey = "morning" | "midday" | "afternoon" | "evening" | "night";

export interface TimeBlockDef {
  key: TimeBlockKey;
  label: string;
  icon: string; // emoji pour affichage rapide
  defaultStart: string; // HH:MM
  defaultEnd: string;
  minStart: string; // borne basse autorisée
  maxEnd: string;   // borne haute autorisée (peut "wrap" pour la nuit)
  wraps?: boolean;  // true pour la nuit (21h → 06h le lendemain)
}

export const TIME_BLOCKS: TimeBlockDef[] = [
  { key: "morning",   label: "Matin",       icon: "🌅", defaultStart: "06:00", defaultEnd: "12:00", minStart: "06:00", maxEnd: "12:00" },
  { key: "midday",    label: "Midi",        icon: "🌞", defaultStart: "12:00", defaultEnd: "14:00", minStart: "12:00", maxEnd: "14:00" },
  { key: "afternoon", label: "Après-midi",  icon: "☀️", defaultStart: "14:00", defaultEnd: "18:00", minStart: "14:00", maxEnd: "18:00" },
  { key: "evening",   label: "Soirée",      icon: "🌆", defaultStart: "18:00", defaultEnd: "21:00", minStart: "18:00", maxEnd: "21:00" },
  { key: "night",     label: "Nuit",        icon: "🌙", defaultStart: "21:00", defaultEnd: "06:00", minStart: "21:00", maxEnd: "06:00", wraps: true },
];

export const blockColumns = (k: TimeBlockKey) => ({
  active: `avail_${k}_active` as const,
  start: `avail_${k}_start` as const,
  end: `avail_${k}_end` as const,
});

// === Services proposés ===
export interface ServiceDef {
  key: string;
  column: string; // colonne DB
  label: string;
  icon: string;
}

export const PRO_SERVICES: ServiceDef[] = [
  { key: "school_dropoff",   column: "service_school_dropoff",   label: "Dépose à l'école",         icon: "🎒" },
  { key: "school_pickup",    column: "service_school_pickup",    label: "Récupération à l'école",   icon: "🏫" },
  { key: "meal",             column: "service_meal",             label: "Repas",                    icon: "🍽️" },
  { key: "snack",            column: "service_snack",            label: "Goûter",                   icon: "🍪" },
  { key: "park_outdoor",     column: "service_park_outdoor",     label: "Sortie parc / plein air",  icon: "🌳" },
  { key: "school_activity",  column: "service_school_activity",  label: "Activité scolaire",        icon: "📚" },
  { key: "extracurricular",  column: "service_extracurricular",  label: "Activité extra-scolaire",  icon: "🎨" },
  { key: "sport_activity",   column: "service_sport_activity",   label: "Activité sportive",        icon: "⚽" },
  { key: "bath",             column: "service_bath",             label: "Bain",                     icon: "🛁" },
  { key: "homework",         column: "service_homework",         label: "Aide aux devoirs",         icon: "📝" },
  { key: "cleaning",         column: "service_cleaning",         label: "Ménage",                   icon: "🧹" },
  { key: "pet_walk",         column: "service_pet_walk",         label: "Sortie des animaux",       icon: "🐶" },
  { key: "other_activity",   column: "service_other_activity",   label: "Autre activité",           icon: "✨" },
];

// === Helpers d'affichage ===
export const formatTime = (t: string | null | undefined) => (t ? t.slice(0, 5) : "");

export const blockSummary = (
  profile: Record<string, any>,
  block: TimeBlockDef,
): string => {
  const cols = blockColumns(block.key);
  if (!profile[cols.active]) return "";
  return `${formatTime(profile[cols.start])} – ${formatTime(profile[cols.end])}`;
};

export const activeBlocks = (profile: Record<string, any>): TimeBlockDef[] =>
  TIME_BLOCKS.filter((b) => profile[blockColumns(b.key).active]);

export const activeServices = (profile: Record<string, any>): ServiceDef[] =>
  PRO_SERVICES.filter((s) => profile[s.column]);
